/**
 * Owner Identity Model for Scoped Persistence
 *
 * This module provides utilities for scoping Redux persist storage by user/environment.
 * This prevents cross-user data bleed on shared devices and ensures data isolation.
 *
 * @module store/owner
 */

/**
 * Owner represents the identity context for scoped localStorage
 */
export interface Owner {
  /** Environment: 'local', 'dev', 'staging', or 'prod' */
  env: string;
  /** User ID from getUserInfo API */
  userId: string;
}

/**
 * Module-level owner reference (initialized after getUserInfo completes)
 */
let currentOwner: Owner | null = null;

/**
 * Gets environment from VITE_BUILD_ENV
 */
export function getEnvironment(): string {
  // Use VITE_BUILD_ENV if available, default to 'dev'
  const env = import.meta.env.VITE_BUILD_ENV as string | undefined;
  return env || 'dev';
}

/**
 * Creates an Owner from user data
 *
 * @param userId - User ID from getUserInfo
 * @returns Owner object
 */
export function createOwner(userId: string): Owner {
  return {
    env: getEnvironment(),
    userId,
  };
}

/**
 * Sets the current owner (called after getUserInfo completes)
 */
export function setOwner(owner: Owner): void {
  currentOwner = owner;
}

/**
 * Gets the current owner (returns null if not yet initialized)
 */
export function getOwner(): Owner | null {
  return currentOwner;
}

/**
 * Clears the owner (called on logout)
 */
export function clearOwner(): void {
  currentOwner = null;
}

/**
 * Generates scoped key prefix for redux-persist
 *
 * @param owner - Owner context
 * @returns Prefix string like "persist:prod:user456:"
 */
export function getScopedKeyPrefix(owner: Owner): string {
  return `persist:${owner.env}:${owner.userId}:`;
}

/**
 * Generates fully scoped storage key
 *
 * @param owner - Owner context
 * @param sliceKey - Redux slice key (e.g., "feature_encounters_encounters/workflow")
 * @returns Full key like "persist:prod:user456:feature_encounters_encounters/workflow"
 */
export function getScopedKey(owner: Owner, sliceKey: string): string {
  return `${getScopedKeyPrefix(owner)}${sliceKey}`;
}

/**
 * Generates legacy (unscoped) storage key for migration
 *
 * @param sliceKey - Redux slice key
 * @returns Legacy key like "persist:feature_encounters_encounters/workflow"
 */
export function getLegacyKey(sliceKey: string): string {
  return `persist:${sliceKey}`;
}

/**
 * Checks if two owners are equal
 */
export function ownersEqual(a: Owner | null, b: Owner | null): boolean {
  if (!a || !b) return a === b;
  return a.env === b.env && a.userId === b.userId;
}
